import {
  mergeMetaPromptsWithFinalStepDefault,
  normalizeLegacyRagDocIds,
  normalizeReminderPromptPosition,
  type AgentConfiguratorState,
} from '../types/agentConfigurator';
import { resolveModelsReasoning } from './gpt5ConfiguratorModels';

/** User-visible when an optimization snapshot has no coordinator / master block */
export const OPTIMIZATION_COORDINATOR_MISSING_MESSAGE =
  'No coordinator/master-agent found in configuration.';

function unwrapOptimizationIterationPayload(result: any): Record<string, unknown> {
  if (!result || typeof result !== 'object') return {};
  const inner = (result as any).data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return result as Record<string, unknown>;
}

export function buildAgentConfiguratorStateFromCoordinatorWorkers(
  coordinatorData: any,
  workersData: any[],
  metaPromptsData?: any,
  asyncOrchestratorData?: any,
  reminderPromptPosition?: unknown
): AgentConfiguratorState {
  const coordModel = coordinatorData.model || coordinatorData.model_name || 'gpt-4.1-mini';
  const coordReasoningRaw =
    coordinatorData.models_reasoning ?? coordinatorData.model_reasoning ?? coordinatorData.modelsReasoning;
  return {
    coordinator: {
      name: coordinatorData.name || 'AgentCoordinator',
      description: coordinatorData.description || '',
      prompt: coordinatorData.prompt || '',
      model: coordModel,
      temperature: coordinatorData.temperature ?? 0.8,
      models_reasoning: resolveModelsReasoning(coordModel, coordReasoningRaw),
      use_rag: coordinatorData.use_rag || false,
      use_ltm: coordinatorData.use_ltm || false,
      use_diary_read: coordinatorData.use_diary_read === true,
      use_diary_write: coordinatorData.use_diary_write === true,
      use_portrait_read: coordinatorData.use_portrait_read === true,
      use_portrait_write: coordinatorData.use_portrait_write === true,
      id: typeof coordinatorData.id === 'string' ? coordinatorData.id : undefined,
      agent_id:
        typeof coordinatorData.agent_id === 'string'
          ? coordinatorData.agent_id
          : typeof coordinatorData.id === 'string'
            ? coordinatorData.id
            : undefined,
      reminder_prompt:
        typeof coordinatorData.reminder_prompt === 'string' ? coordinatorData.reminder_prompt : undefined,
      rag_doc_ids: normalizeLegacyRagDocIds(coordinatorData.rag_doc_ids),
    },
    workers: workersData.map((w, i) => {
      const wm = w.model || w.model_name || 'gpt-4.1-mini';
      const wr = w.models_reasoning ?? w.model_reasoning ?? w.modelsReasoning;
      return {
        id: String(w.id || i + 1),
        name: w.name || w.agent_name || w.worker_name || `Worker ${i + 1}`,
        description: w.description || '',
        prompt: w.prompt || '',
        mode: (w.mode === 'asynchronous' ? 'asynchronous' : 'synchronous') as 'synchronous' | 'asynchronous',
        model: wm,
        temperature: w.temperature ?? 0.7,
        models_reasoning: resolveModelsReasoning(wm, wr),
        use_rag: w.use_rag || false,
        use_ltm: w.use_ltm || false,
        use_diary_read: w.use_diary_read === true,
        use_diary_write: w.use_diary_write === true,
        use_portrait_read: w.use_portrait_read === true,
        use_portrait_write: w.use_portrait_write === true,
        is_trainable: typeof w.is_trainable === 'boolean' ? w.is_trainable : true,
        reminder_prompt: typeof w.reminder_prompt === 'string' ? w.reminder_prompt : undefined,
        rag_doc_ids: normalizeLegacyRagDocIds(w.rag_doc_ids),
      };
    }),
    metaPrompts: mergeMetaPromptsWithFinalStepDefault(
      metaPromptsData && typeof metaPromptsData === 'object'
        ? (Object.fromEntries(
            Object.entries(metaPromptsData).filter(([, value]) => typeof value === 'string')
          ) as Record<string, string>)
        : null
    ),
    async_orchestrator:
      asyncOrchestratorData && typeof asyncOrchestratorData === 'object'
        ? { ...asyncOrchestratorData }
        : undefined,
    reminder_prompt_position: normalizeReminderPromptPosition(reminderPromptPosition),
  };
}

/** Maps a multi-agent WebSocket iteration payload to configurator state. */
export function buildAgentConfiguratorStateFromMultiAgentIterationResult(
  result: any
): AgentConfiguratorState | null {
  const u = unwrapOptimizationIterationPayload(result) as any;
  const master = u?.['master-agent'] || u?.master_agent || u?.coordinator;
  if (!master) return null;
  const workersRaw = u?.['worker-agents'] || u?.worker_agents || u?.workers;
  const workers = Array.isArray(workersRaw) ? workersRaw : [];
  const meta = u?.meta_prompts || u?.metaPrompts;
  const asyncOrchestrator = u?.async_orchestrator || u?.asyncOrchestrator;
  const reminderPos = u?.reminder_prompt_position ?? u?.reminderPromptPosition;
  return buildAgentConfiguratorStateFromCoordinatorWorkers(
    master,
    workers,
    meta,
    asyncOrchestrator,
    reminderPos
  );
}

function iterationSeq(result: any): number | undefined {
  if (typeof result?.seq === 'number') return result.seq;
  if (typeof result?.iteration === 'number') return result.iteration;
  return undefined;
}

export function getOptimizationBaselineResult(results: any[]): any | undefined {
  return results.find((result) => iterationSeq(result) === 0);
}

export function getOptimizationStepResults(results: any[]): any[] {
  return results
    .filter((result) => {
      const seq = iterationSeq(result);
      return seq !== undefined && seq >= 1;
    })
    .sort((a, b) => (iterationSeq(a) ?? 0) - (iterationSeq(b) ?? 0));
}

export function resolveOptimizationCompareConfigs(params: {
  iterationResults: any[];
  selectedStepIndex: number;
  fallbackInitialConfig?: AgentConfiguratorState | null;
}): {
  before: AgentConfiguratorState | null;
  after: AgentConfiguratorState | null;
  stepLabel: string;
} {
  const optimizedResults = getOptimizationStepResults(params.iterationResults);
  const baselineResult = getOptimizationBaselineResult(params.iterationResults);

  const before =
    (baselineResult ? buildAgentConfiguratorStateFromMultiAgentIterationResult(baselineResult) : null) ??
    params.fallbackInitialConfig ??
    null;

  const safeIndex = Math.max(
    0,
    Math.min(params.selectedStepIndex, Math.max(optimizedResults.length - 1, 0))
  );
  const stepResult = optimizedResults[safeIndex];
  const after = stepResult ? buildAgentConfiguratorStateFromMultiAgentIterationResult(stepResult) : null;
  const seq = stepResult ? iterationSeq(stepResult) : undefined;

  return {
    before,
    after,
    stepLabel: typeof seq === 'number' ? `Step ${seq}` : 'Selected step',
  };
}

export function hasOptimizationCompareData(results: any[]): boolean {
  return getOptimizationStepResults(results).length > 0;
}
